import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import {
  EXCHANGE_MODE_OPTIONS,
  ONLINE_FULFILLMENT_OPTIONS,
  SERVICE_DELIVERY_MULTI_OPTIONS,
  SERVICE_DELIVERY_OPTIONS,
  SERVICE_TERMS_SUMMARY,
  SERVICE_URGENCY_OPTIONS,
  SETTLEMENT_CURRENCY_OPTIONS,
  SETTLEMENT_METHOD_OPTIONS,
} from '../../config/exchangeOnlineOptions';
import {
  DEMO_SERVICE_PROVIDERS,
  filterDemoProviders,
  getCategoryLabel,
} from '../../config/serviceProviderCategories';
import { getSimpleAppExperience } from '../../config/simpleAppExperiences';
import { useActivityCart } from '../../context/ActivityCartContext';
import { useJobBoard } from '../../context/JobBoardContext';
import { useReputation } from '../../context/ReputationContext';
import {
  fieldsSatisfied,
  resolveFormSteps,
  shouldOfferProgressive,
} from '../../util/progressiveForm';
import { scheduleWindowOptions, MATCH_MODE_OPTIONS, ESCROW_MILESTONE_OPTIONS } from '../../util/jobLifecycle';
import { CommunityAskBar } from '../CommunityChannels/CommunityChannels';
import ColdStartModes from './ColdStartModes';
import JobWorkspace from './JobWorkspace';
import ProgressiveFormChrome from './ProgressiveFormChrome';
import ServiceCategoryOptions from './ServiceCategoryOptions';
import IdentitySyncBar from './IdentitySyncBar';
import SupplyWaitlistForm from './SupplyWaitlistForm';

import css from './SimpleAppExperience.module.css';
import svcCss from './ServicesAppExperience.module.css';

const TABS = {
  provider: 'provider',
};

const STAGES = {
  explore: 'explore',
  detail: 'detail',
  create: 'create',
  become: 'become',
  review: 'review',
  job: 'job',
  requests: 'requests',
  success: 'success',
};

const mapSelect = opts =>
  opts.map(o => ({
    id: o.value,
    label: o.label,
    value: o.value,
  }));

const OPTIONS_BY_SOURCE = {
  exchangeMode: mapSelect(EXCHANGE_MODE_OPTIONS),
  settlementMethods: SETTLEMENT_METHOD_OPTIONS,
  onlineFulfillment: ONLINE_FULFILLMENT_OPTIONS,
  settlementCurrency: mapSelect(SETTLEMENT_CURRENCY_OPTIONS),
  serviceDelivery: mapSelect(SERVICE_DELIVERY_OPTIONS),
  serviceDeliveryMulti: SERVICE_DELIVERY_MULTI_OPTIONS,
  serviceUrgency: mapSelect(SERVICE_URGENCY_OPTIONS),
  scheduleWindow: mapSelect(scheduleWindowOptions),
  matchMode: mapSelect(MATCH_MODE_OPTIONS),
  escrowMilestone: mapSelect(ESCROW_MILESTONE_OPTIONS),
};

const emptyForm = () => ({
  multiselect: {},
  termsAccepted: false,
});

const resolveFieldOptions = field => {
  if (field.optionsSource && OPTIONS_BY_SOURCE[field.optionsSource]) {
    return OPTIONS_BY_SOURCE[field.optionsSource];
  }
  return (field.options || []).map(o => ({
    id: o.value,
    label: o.label,
    value: o.value,
  }));
};

const optionLabel = (field, value) => {
  if (field.type === 'serviceCategory') {
    return getCategoryLabel(value);
  }
  const opts = resolveFieldOptions(field);
  return opts.find(o => (o.value || o.id) === value)?.label || value;
};

/**
 * Services hub — provider directory, request, and become flows.
 */
const ServicesAppExperience = ({ app }) => {
  const experience = getSimpleAppExperience(app.id);
  const { upsertDraft } = useActivityCart();
  const { createJob, listJobs, statusLabel, syncStatus } = useJobBoard();
  const { getProviderStats, ensureProfile } = useReputation();
  const location = useLocation();

  const tab = TABS.provider;
  const [stage, setStage] = useState(STAGES.explore);
  const [query, setQuery] = useState('');
  const [categoryId, setCategoryId] = useState('all');
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formStepIndex, setFormStepIndex] = useState(0);
  const [onePager, setOnePager] = useState(false);
  const [formMode, setFormMode] = useState('create'); // create | become
  const [activeJobId, setActiveJobId] = useState(null);

  useEffect(() => {
    const jobId = new URLSearchParams(location.search).get('job');
    if (jobId) {
      setActiveJobId(jobId);
      setStage(STAGES.job);
    }
  }, [location.search]);

  useEffect(() => {
    if (selected?.id) {
      ensureProfile(selected.id, selected);
    }
  }, [selected, ensureProfile]);

  useEffect(() => {
    DEMO_SERVICE_PROVIDERS.forEach(p => ensureProfile(p.id, p));
  }, [ensureProfile]);

  const providerCopy = experience?.tabs?.provider || {};
  const activeFields =
    formMode === 'become'
      ? providerCopy.becomeFormFields || []
      : providerCopy.formFields || [];
  const activeStepsDef =
    formMode === 'become' ? providerCopy.becomeFormSteps : providerCopy.formSteps;

  const formSteps = useMemo(
    () => resolveFormSteps(activeFields, activeStepsDef),
    [activeFields, activeStepsDef]
  );
  const progressive = shouldOfferProgressive(formSteps);
  const visibleFields =
    !progressive || onePager ? activeFields : formSteps[formStepIndex]?.fields || [];
  const isLastFormStep = !progressive || onePager || formStepIndex >= formSteps.length - 1;
  const multiselect = formData.multiselect || {};

  const filteredProviders = useMemo(
    () => filterDemoProviders(query, categoryId),
    [query, categoryId]
  );

  const myJobs = useMemo(
    () => listJobs({ appId: app.id, kind: 'services' }),
    [listJobs, app.id]
  );

  const canAdvanceStep = useMemo(
    () => fieldsSatisfied(visibleFields, formData, multiselect),
    [visibleFields, formData, multiselect]
  );

  const canSubmit = useMemo(
    () => fieldsSatisfied(activeFields, formData, multiselect),
    [activeFields, formData, multiselect]
  );

  const canConfirm =
    Boolean(selected) || (canSubmit && (!providerCopy.requireTerms || formData.termsAccepted));

  const noProviderResults = stage === STAGES.explore && filteredProviders.length === 0;

  if (!experience) {
    return null;
  }

  const persist = (status, title, data) => {
    upsertDraft({
      appId: app.id,
      title,
      stepIndex: 0,
      formData: { tab, formMode, ...data },
      status,
      resumePath: `${app.hubPath}?from=landing`,
      launchHref: app.launchHref,
    });
  };

  const finish = (title, data) => {
    persist('completed', title, data);
    setStage(STAGES.success);
  };

  const resetExplore = () => {
    setSelected(null);
    setFormData(emptyForm());
    setFormStepIndex(0);
    setOnePager(false);
    setFormMode('create');
    setActiveJobId(null);
    setStage(STAGES.explore);
  };

  const openJob = jobId => {
    setActiveJobId(jobId);
    setStage(STAGES.job);
  };

  const publishRequest = () => {
    if (!canConfirm && !selected) {
      return;
    }
    const title =
      selected?.name ||
      formData.displayName ||
      getCategoryLabel(formData.categoryId || formData.becomeCategory) ||
      'Service request';
    const brief = selected
      ? {
          categoryId: selected.categoryId,
          providerId: selected.id,
          notes: `Request for ${selected.name}`,
          scheduleWindow: formData.scheduleWindow,
        }
      : { ...formData };
    const job = createJob({
      appId: app.id,
      kind: 'services',
      title: typeof title === 'string' ? title : 'Service request',
      brief,
      scheduleWindow:
        scheduleWindowOptions.find(o => o.value === formData.scheduleWindow)?.label ||
        formData.scheduleWindow ||
        '',
      demoProviders: DEMO_SERVICE_PROVIDERS.filter(p =>
        selected
          ? p.categoryId === selected.categoryId
          : !brief.categoryId || p.categoryId === brief.categoryId
      ),
      autoQuotes: formMode !== 'become',
      matchMode: formData.matchMode || 'best_quote',
    });
    upsertDraft({
      appId: app.id,
      title: job.title,
      stepIndex: 0,
      formData: { tab, formMode, jobId: job.id, ...brief },
      status: 'in_progress',
      resumePath: `${app.hubPath}?from=landing`,
      launchHref: app.launchHref,
    });
    if (formMode === 'become') {
      finish(job.title, { ...formData, jobId: job.id });
      return;
    }
    openJob(job.id);
  };

  const startCreate = () => {
    setSelected(null);
    setFormData(emptyForm());
    setFormStepIndex(0);
    setOnePager(false);
    setFormMode('create');
    setStage(STAGES.create);
  };

  const startBecome = () => {
    setSelected(null);
    setFormData(emptyForm());
    setFormStepIndex(0);
    setOnePager(false);
    setFormMode('become');
    setStage(STAGES.become);
  };

  const backFromForm = () => {
    if (progressive && !onePager && formStepIndex > 0) {
      setFormStepIndex(i => i - 1);
      return;
    }
    resetExplore();
  };

  const advanceForm = () => {
    if (!canAdvanceStep) {
      return;
    }
    if (!isLastFormStep) {
      setFormStepIndex(i => i + 1);
      return;
    }
    if (formMode === 'become') {
      if (!canSubmit) {
        return;
      }
      setStage(STAGES.review);
      return;
    }
    if (!canSubmit) {
      return;
    }
    setStage(STAGES.review);
  };

  const toggleMulti = (fieldId, optionId) => {
    setFormData(prev => {
      const current = new Set(prev.multiselect?.[fieldId] || []);
      if (current.has(optionId)) {
        current.delete(optionId);
      } else {
        current.add(optionId);
      }
      return {
        ...prev,
        multiselect: { ...(prev.multiselect || {}), [fieldId]: [...current] },
      };
    });
  };

  const renderField = field => {
    if (field.type === 'serviceCategory') {
      return (
        <label key={field.id} className={css.field}>
          <span className={css.fieldLabel}>
            {field.label}
            {field.required ? ' *' : ''}
          </span>
          <select
            className={css.input}
            value={formData[field.id] || ''}
            onChange={e => setFormData(prev => ({ ...prev, [field.id]: e.target.value }))}
            data-testid={field.id === 'becomeCategory' ? 'services-become-category' : undefined}
          >
            <ServiceCategoryOptions includeEmpty emptyLabel="Choose…" />
          </select>
        </label>
      );
    }

    if (field.type === 'multiselect') {
      const opts = resolveFieldOptions(field);
      const selectedIds = multiselect[field.id] || [];
      return (
        <div key={field.id} className={css.field}>
          <span className={css.fieldLabel}>
            {field.label}
            {field.required ? ' *' : ''}
          </span>
          <div className={svcCss.addonChips} role="group" aria-label={field.label}>
            {opts.map(opt => {
              const id = opt.id || opt.value;
              const on = selectedIds.includes(id);
              return (
                <button
                  key={id}
                  type="button"
                  className={on ? svcCss.addonChipActive : svcCss.addonChip}
                  aria-pressed={on}
                  onClick={() => toggleMulti(field.id, id)}
                >
                  {on ? '✓ ' : ''}
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    if (field.type === 'textarea') {
      return (
        <label key={field.id} className={css.field}>
          <span className={css.fieldLabel}>
            {field.label}
            {field.required ? ' *' : ''}
          </span>
          <textarea
            className={css.input}
            rows={3}
            placeholder={field.placeholder}
            value={formData[field.id] || ''}
            onChange={e => setFormData(prev => ({ ...prev, [field.id]: e.target.value }))}
          />
        </label>
      );
    }

    if (field.type === 'select') {
      const opts = resolveFieldOptions(field);
      return (
        <label key={field.id} className={css.field}>
          <span className={css.fieldLabel}>
            {field.label}
            {field.required ? ' *' : ''}
          </span>
          <select
            className={css.input}
            value={formData[field.id] || ''}
            onChange={e => setFormData(prev => ({ ...prev, [field.id]: e.target.value }))}
          >
            <option value="">Choose…</option>
            {opts.map(opt => (
              <option key={opt.value || opt.id} value={opt.value || opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      );
    }

    return (
      <label key={field.id} className={css.field}>
        <span className={css.fieldLabel}>
          {field.label}
          {field.required ? ' *' : ''}
        </span>
        <input
          className={css.input}
          type="text"
          placeholder={field.placeholder}
          value={formData[field.id] || ''}
          onChange={e => setFormData(prev => ({ ...prev, [field.id]: e.target.value }))}
        />
      </label>
    );
  };

  const renderFormPanel = (title, testId) => (
    <section className={css.panel} data-testid={testId}>
      <h2 className={css.sectionTitle}>{title}</h2>
      {formMode === 'become' ? (
        <p className={css.cardBlurb}>Same category list customers search — on-site or online.</p>
      ) : null}
      {progressive ? (
        <ProgressiveFormChrome
          steps={formSteps}
          stepIndex={formStepIndex}
          onePager={onePager}
          onToggleOnePager={() => {
            setOnePager(v => !v);
            setFormStepIndex(0);
          }}
          onSelectStep={setFormStepIndex}
        />
      ) : null}
      <div className={css.fields}>{visibleFields.map(renderField)}</div>
      <div className={css.actionRow} style={{ marginTop: 16 }}>
        <button type="button" className={css.secondaryBtn} onClick={backFromForm}>
          Back
        </button>
        <button
          type="button"
          className={css.primaryBtn}
          disabled={isLastFormStep ? !canSubmit : !canAdvanceStep}
          onClick={advanceForm}
        >
          {isLastFormStep ? 'Continue' : 'Next'}
        </button>
      </div>
      {formMode === 'create' ? (
        <CommunityAskBar
          appId={app.id}
          prompt="Can’t find the right trade? Talk with the community"
        />
      ) : null}
    </section>
  );

  return (
    <div
      className={css.root}
      style={{ '--app-accent': app.accent }}
      data-testid="simple-app-services"
    >
      <header className={css.hero}>
        <h1 className={css.title}>{experience.headline}</h1>
        <p className={css.promise}>{experience.promise}</p>
      </header>

      {stage === STAGES.explore ? (
        <>
          <ColdStartModes
            appId={app.id}
            onRequest={startCreate}
            requestLabel={providerCopy.createCta || 'Request a provider'}
          />
          <div className={svcCss.searchBlock}>
            <label className={svcCss.searchLabel} htmlFor="provider-search">
              Find a provider
            </label>
            <input
              id="provider-search"
              className={css.input}
              type="search"
              placeholder="Search trade, name, or area…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              data-testid="services-provider-search"
            />
            <select
              className={css.input}
              value={categoryId}
              onChange={e => setCategoryId(e.target.value)}
              aria-label="Category"
              data-testid="services-provider-category"
            >
              <option value="all">All categories</option>
              <ServiceCategoryOptions />
            </select>
          </div>

          {noProviderResults ? (
            <div className={svcCss.empty} data-testid="services-provider-empty">
              <p className={svcCss.emptyTitle}>No providers match that search</p>
              <p className={svcCss.emptyBody}>
                Try another category — or ask the community on WhatsApp or Telegram. Moderators can
                help connect you.
              </p>
              <CommunityAskBar appId={app.id} prompt="Talk with the community" />
            </div>
          ) : (
            <section>
              <h2 className={css.sectionTitle}>{providerCopy.exploreTitle || 'Providers nearby'}</h2>
              <p className={css.demoBanner}>
                Operator seed catalog · {DEMO_SERVICE_PROVIDERS.length} profiles — tap to request
              </p>
              <div className={css.cardList}>
                {filteredProviders.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    className={css.card}
                    onClick={() => {
                      setSelected(p);
                      setStage(STAGES.detail);
                    }}
                    data-testid={`services-provider-${p.id}`}
                  >
                    {p.image ? (
                      <img className={css.cardMedia} src={p.image} alt="" loading="lazy" />
                    ) : null}
                    <div className={css.cardBody}>
                      <div className={css.cardTop}>
                        <h3 className={css.cardTitle}>{p.name}</h3>
                        <span className={css.badge}>{p.badge}</span>
                      </div>
                      <p className={css.cardMeta}>
                        {getCategoryLabel(p.categoryId)} · {p.area}
                      </p>
                      <p className={css.cardBlurb}>{p.blurb}</p>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}

          <div className={css.actionRow}>
            <button
              type="button"
              className={css.primaryBtn}
              onClick={startCreate}
              data-testid="services-request-provider"
            >
              {providerCopy.createCta || 'Request a provider'}
            </button>
          </div>
          {myJobs.length ? (
            <section style={{ marginTop: 18 }} data-testid="services-my-requests">
              <h2 className={css.sectionTitle}>My requests</h2>
              <IdentitySyncBar syncStatus={syncStatus} compact />
              <ul className={css.requestsList}>
                {myJobs.slice(0, 6).map(j => (
                  <li key={j.id}>
                    <button type="button" className={css.requestItem} onClick={() => openJob(j.id)}>
                      <div>
                        <p className={css.requestTitle}>{j.title}</p>
                        <p className={css.requestMeta}>
                          {statusLabel(j.status)}
                          {j.providerName ? ` · ${j.providerName}` : ''}
                          {j.quotes?.length ? ` · ${j.quotes.length} quote(s)` : ''}
                        </p>
                      </div>
                      <span className={css.badge}>Open</span>
                    </button>
                  </li>
                ))}
              </ul>
              <p className={css.secondaryLinkWrap}>
                <Link to="/activity" className={css.secondaryLink}>
                  All activity
                </Link>
              </p>
            </section>
          ) : null}
          <p className={css.secondaryLinkWrap}>
            <button
              type="button"
              className={css.secondaryLink}
              onClick={startBecome}
              data-testid="services-become-provider"
            >
              Become a provider
            </button>
          </p>
          <div style={{ marginTop: 20 }}>
            <SupplyWaitlistForm kind="services" />
          </div>
          {!noProviderResults ? <CommunityAskBar appId={app.id} /> : null}
        </>
      ) : null}

      {stage === STAGES.detail && selected ? (
        <section className={css.panel}>
          <p className={css.demoBanner}>Provider storefront</p>
          {selected.image ? (
            <img className={css.detailMedia} src={selected.image} alt="" />
          ) : null}
          <h2 className={css.sectionTitle}>{selected.name}</h2>
          <p className={css.cardMeta}>
            {getCategoryLabel(selected.categoryId)} · {selected.area}
            {selected.years ? ` · ${selected.years} yrs` : ''}
            {getProviderStats(selected.id).avgRating != null
              ? ` · ★ ${getProviderStats(selected.id).avgRating} (${
                  getProviderStats(selected.id).count
                })`
              : ' · New'}
          </p>
          <p className={css.cardBlurb}>{selected.blurb}</p>
          {selected.rateHint ? (
            <p className={css.cardMeta} style={{ marginTop: 8 }}>
              Rates: {selected.rateHint}
            </p>
          ) : null}
          <div className={css.storefrontMeta}>
            {(selected.deliveryModes || []).map(m => (
              <span key={m} className={css.storefrontChip}>
                {m.replace(/_/g, ' ')}
              </span>
            ))}
            {(selected.settlement || []).slice(0, 4).map(s => (
              <span key={s} className={css.storefrontChip}>
                {s.replace(/_/g, ' ')}
              </span>
            ))}
            {getProviderStats(selected.id).badges.map(b => (
              <span key={b} className={css.storefrontChip}>
                {b}
              </span>
            ))}
          </div>
          {selected.portfolio?.length ? (
            <ul className={css.portfolioList}>
              {selected.portfolio.map(item => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : null}
          <div className={css.actionRow} style={{ marginTop: 16 }}>
            <button type="button" className={css.secondaryBtn} onClick={resetExplore}>
              Back
            </button>
            <button
              type="button"
              className={css.primaryBtn}
              onClick={() => setStage(STAGES.review)}
            >
              Request this provider
            </button>
          </div>
        </section>
      ) : null}

      {stage === STAGES.create
        ? renderFormPanel(providerCopy.createTitle || 'What do you need?')
        : null}

      {stage === STAGES.become
        ? renderFormPanel('Become a provider', 'services-become-form')
        : null}

      {stage === STAGES.job && activeJobId ? (
        <JobWorkspace
          jobId={activeJobId}
          onBackToList={resetExplore}
          onDone={() => setStage(STAGES.success)}
        />
      ) : null}

      {stage === STAGES.review ? (
        <section className={css.panel}>
          <h2 className={css.sectionTitle}>Quick check</h2>
          <p className={css.demoBanner}>
            {formMode === 'become'
              ? 'Confirm your provider profile'
              : 'Posting opens quotes from matching providers'}
          </p>
          <ul className={css.summaryList}>
            <li className={css.summaryRow}>
              <span className={css.summaryKey}>Type</span>
              <span className={css.summaryVal}>
                {formMode === 'become' ? 'Become a provider' : 'Service request'}
              </span>
            </li>
            {selected ? (
              <li className={css.summaryRow}>
                <span className={css.summaryKey}>Choice</span>
                <span className={css.summaryVal}>{selected.name}</span>
              </li>
            ) : null}
            {!selected
              ? activeFields.map(field => {
                  if (field.type === 'multiselect') {
                    const values = multiselect[field.id] || [];
                    if (!values.length) {
                      return null;
                    }
                    return (
                      <li key={field.id} className={css.summaryRow}>
                        <span className={css.summaryKey}>{field.label}</span>
                        <span className={css.summaryVal}>
                          {values.map(v => optionLabel(field, v)).join(', ')}
                        </span>
                      </li>
                    );
                  }
                  if (!formData[field.id]) {
                    return null;
                  }
                  return (
                    <li key={field.id} className={css.summaryRow}>
                      <span className={css.summaryKey}>{field.label}</span>
                      <span className={css.summaryVal}>
                        {field.type === 'select' || field.type === 'serviceCategory'
                          ? optionLabel(field, formData[field.id])
                          : formData[field.id]}
                      </span>
                    </li>
                  );
                })
              : null}
          </ul>
          {providerCopy.requireTerms && !selected ? (
            <label className={svcCss.termsBox} data-testid="services-terms">
              <input
                type="checkbox"
                checked={Boolean(formData.termsAccepted)}
                onChange={e =>
                  setFormData(prev => ({ ...prev, termsAccepted: e.target.checked }))
                }
              />
              <span>{SERVICE_TERMS_SUMMARY}</span>
            </label>
          ) : null}
          <div className={css.actionRow} style={{ marginTop: 16 }}>
            <button
              type="button"
              className={css.secondaryBtn}
              onClick={() =>
                setStage(
                  selected
                    ? STAGES.detail
                    : formMode === 'become'
                      ? STAGES.become
                      : STAGES.create
                )
              }
            >
              Back
            </button>
            <button
              type="button"
              className={css.primaryBtn}
              disabled={!canConfirm && !selected}
              onClick={publishRequest}
              data-testid="services-publish-request"
            >
              {formMode === 'become' && !selected ? 'Join as provider' : 'Post request & get quotes'}
            </button>
          </div>
        </section>
      ) : null}

      {stage === STAGES.success ? (
        <section className={css.panel} data-testid="services-success">
          <div className={css.successIcon} aria-hidden>
            ✓
          </div>
          <h2 className={css.sectionTitle}>{experience.successTitle || 'Request ready'}</h2>
          <p className={css.cardBlurb}>{experience.successBody}</p>
          <div className={css.actionRow} style={{ marginTop: 16 }}>
            <button type="button" className={css.primaryBtn} onClick={resetExplore}>
              Back to Services
            </button>
            <Link to="/landing" className={css.secondaryBtn}>
              All apps
            </Link>
          </div>
          <CommunityAskBar appId={app.id} prompt="Talk with the community" />
        </section>
      ) : null}

      <details className={css.techDetails}>
        <summary className={css.techSummary}>How it works behind the scenes</summary>
        <div className={css.techBody}>
          <ul>
            {(experience.technicalNotes || []).map(note => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      </details>
    </div>
  );
};

export default ServicesAppExperience;
